import React, { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePathname, useRouter } from 'next/navigation';
import AuthRefreshMonitor from '../auth/AuthRefreshMonitor';
import AuthStatus from '../auth/AuthStatus';
import AuthStateRecovery from '../auth/AuthStateRecovery';
import dynamic from 'next/dynamic';
import { SparklesIcon } from '@heroicons/react/24/outline';
import CommandPalette from '../command/CommandPalette';
import { Toaster } from '@/components/ui/Toaster';

// Dynamically import PWA components to avoid SSR issues
const PWAInstallPrompt = dynamic(() => import('../pwa/PWAInstallPrompt'), { 
  ssr: false 
});

const UpdateNotification = dynamic(() => import('../pwa/UpdateNotification'), {
  ssr: false
});

interface MainLayoutProps {
  children: ReactNode;
}

// Liquid glass loading component
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
    {/* Liquid glass background */}
    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/5 to-indigo-600/10 animate-shimmer"></div>
    <div className="absolute inset-0 liquid-glass" style={{
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 50%, rgba(255, 255, 255, 0.06) 100%)',
      backdropFilter: 'blur(20px) saturate(1.8) brightness(1.1)',
      border: 'none'
    }}></div>
    
    <div className="relative z-10 text-center space-y-8">
      {/* Glass loading spinner container */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-2xl animate-pulse"></div>
        <div className="relative liquid-glass rounded-3xl p-8 border border-white/10">
          <div className="w-16 h-16 mx-auto relative">
            <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 border-r-purple-500 rounded-full animate-spin"></div>
            <div className="absolute inset-2 border-2 border-transparent border-b-indigo-400 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
            <SparklesIcon className="h-6 w-6 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
          </div>
        </div>
      </div>
      
      {/* Glass text container */}
      <div className="space-y-4">
        <div className="glass-content-creation rounded-2xl p-6 border border-indigo-200/20 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-foreground mb-2">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">MiComms</span>
          </h3>
          <p className="text-sm text-muted-foreground">Preparing your PR workspace...</p>
        </div>
      </div>
    </div>
  </div>
);

export default function MainLayout({ children }: MainLayoutProps) {
  const { currentUser, loading, refreshToken } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const [isAppReady, setIsAppReady] = useState(false);
  const isPublicRoute = pathname === '/welcome' || pathname === '/login';
  const shouldRedirectToWelcome =
    !loading &&
    isAppReady &&
    !currentUser &&
    !isPublicRoute &&
    process.env.NEXT_PUBLIC_E2E_TEST !== '1';
  
  // Try to refresh token when layout mounts (silent auth check)
  useEffect(() => {
    if (currentUser) {
      (async () => {
        try {
          await refreshToken();
        } catch (err) {
          console.error("Auth token refresh failed on layout mount:", err);
        } finally {
          // Small delay to ensure smooth loading transition
          setTimeout(() => setIsAppReady(true), 500);
        }
      })();
    } else {
      setIsAppReady(true);
    }
  }, [currentUser, refreshToken]);
  
  // Check for service worker updates
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
      });

      // Listen for service worker update messages
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_UPDATED') {
          console.log('Service Worker updated to version:', event.data.version);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (shouldRedirectToWelcome) {
      router.replace('/welcome');
    }
  }, [router, shouldRedirectToWelcome]);
  
  // Show loading state
  if (loading || !isAppReady) {
    return <LoadingScreen />;
  }
  
  // Show login page if not authenticated (allow bypass during E2E tests)
  // Public routes that don't require authentication
  if (!currentUser && !isPublicRoute) {
    if (process.env.NEXT_PUBLIC_E2E_TEST === '1') {
      // Allow app to render for test flows without real auth
    } else {
      // Redirect unauthenticated users to the landing page
      return <LoadingScreen />;
    }
  }

  // Public routes get a bare layout (no app shell, nav, or background chrome)
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Show main layout - navigation is now handled by root layout.tsx
  return (
    <>
      {/* iOS 26 inspired background with liquid glass effect */}
      {/* Background visuals (kept behind content to avoid wash-out) */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        {/* Primary background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/95 to-background/90" />

        {/* Liquid glass texture overlay */}
        <div className="absolute inset-0 opacity-10 md:opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-secondary/[0.02]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.04)_0%,transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.02)_0%,transparent_50%)]" />
        </div>

        {/* Subtle animated overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-background/[0.01] to-transparent animate-pulse opacity-10 md:opacity-20"
             style={{ animationDuration: '4s' }} />
      </div>

      {/* Content passed from layout.tsx */}
      {children}

      {/* PWA Components */}
      <PWAInstallPrompt />
      <UpdateNotification />

      {/* Auth monitoring components */}
      <AuthRefreshMonitor />
      <AuthStatus />
      <AuthStateRecovery />

      {/* Command Palette */}
      <CommandPalette />

      {/* Global Toaster (notifications + confirm) */}
      <Toaster />
    </>
  );
}
